import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiGlobe, FiZap, FiCpu, FiGrid, FiRefreshCw, FiMessageSquare, FiSend, FiServer, FiShare2, FiCheckSquare } from 'react-icons/fi';
import SkillEditorBody from '../../components/SkillEditorBody';
import AgentBundlePreview from '../../components/AgentBundlePreview';
type QueueStatus = 'pending' | 'changes_requested' | 'approved' | 'rejected';
type ItemType = 'skill' | 'agent' | 'workflow' | 'widget' | 'mcp_server' | 'integration';
type CreatorStatus = 'online' | 'busy' | 'offline';
interface QueueItem {
    id: string;
    toolId?: string;
    name: string;
    version?: string;
    isPriority?: boolean;
    type: ItemType;
    creator: string;
    creatorStatus: CreatorStatus;
    visibility: 'public' | 'agency' | 'private' | 'just_me' | 'my_agency' | 'published';
    submittedAt: string;
    status: QueueStatus;
    description?: string;
    category?: string;
    icon?: string;
    skillsText?: string;
    kbFiles?: string;
    skillContent?: string;
    mcpUrl?: string;
    mcpHeaders?: string;
    agentShareToken?: string;
}
interface ReviewComment {
    id: string;
    authorName: string;
    message: string;
    // Set only on the note auto-posted alongside a Request Changes/Reject verdict -
    // lets the thread visually flag it as the official reason, not an ordinary reply.
    relatedStatus?: 'changes_requested' | 'rejected' | null;
    createdAt: string;
}
function timeAgo(iso: string): string {
    const ms = Date.now() - new Date(iso).getTime();
    const m = Math.floor(ms / 60000);
    if (m < 60)
        return `${m}m ago`;
    const h = Math.floor(m / 60);
    if (h < 24)
        return `${h}h ago`;
    return `${Math.floor(h / 24)}d ago`;
}
function visLabel(v: QueueItem['visibility']): string {
    switch (v) {
        case 'just_me': return 'Personal';
        case 'my_agency':
        case 'agency': return 'My Agency';
        case 'published': return 'Published';
        case 'public': return 'Public';
        case 'private': return 'Private';
        default: return v;
    }
}
function authHeaders() {
    return {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${localStorage.getItem('authToken') || ''}`,
    };
}
const STATUS_CONFIG: Record<QueueStatus, {
    label: string;
    bg: string;
    border: string;
    color: string;
}> = {
    pending: { label: 'Pending', bg: 'rgba(245,158,11,0.15)', border: 'rgba(245,158,11,0.4)', color: '#d97706' },
    changes_requested: { label: 'Changes Requested', bg: 'rgba(249,115,22,0.15)', border: 'rgba(249,115,22,0.4)', color: '#ea580c' },
    approved: { label: 'Approved', bg: 'rgba(16,185,129,0.15)', border: 'rgba(16,185,129,0.4)', color: '#059669' },
    rejected: { label: 'Rejected', bg: 'rgba(239,68,68,0.15)', border: 'rgba(239,68,68,0.4)', color: '#dc2626' },
};
const TYPE_CONFIG: Record<ItemType, {
    Icon: React.ComponentType<{
        size?: number;
    }>;
    color: string;
    label: string;
}> = {
    skill: { Icon: FiZap, color: '#8b5cf6', label: 'Skill' },
    agent: { Icon: FiCpu, color: '#14b8a6', label: 'Agent' },
    workflow: { Icon: FiRefreshCw, color: '#22d3ee', label: 'Workflow' },
    widget: { Icon: FiGrid, color: '#f97316', label: 'Widget' },
    mcp_server: { Icon: FiServer, color: '#3b82f6', label: 'MCP Server' },
    integration: { Icon: FiShare2, color: '#ec4899', label: 'Integration' },
};
const CREATOR_DOT: Record<CreatorStatus, string> = {
    online: '#f59e0b', busy: '#8b5cf6', offline: '#6b7280',
};
const FieldLabel: React.FC<{
    children: React.ReactNode;
}> = ({ children }) => (<div style={{ color: 'var(--text-tertiary)', fontSize: 10, fontWeight: 600, letterSpacing: '0.07em', textTransform: 'uppercase', marginBottom: 6 }}>
    {children}
  </div>);
const FieldBlock: React.FC<{
    label: string;
    value?: string;
}> = ({ label, value }) => (<div>
    <FieldLabel>{label}</FieldLabel>
    <div style={{
        background: 'var(--surface-glass)', border: '1px solid var(--border-glass)',
        borderRadius: 9, padding: '9px 13px',
    }}>
      <span style={{ color: value ? 'var(--text-primary)' : 'var(--text-muted)', fontSize: 13, fontStyle: value ? 'normal' : 'italic' }}>
        {value || '—'}
      </span>
    </div>
  </div>);
const ReviewDetail: React.FC<{
    item: QueueItem;
    onBack: () => void;
    onStatusChange: (id: string, status: QueueStatus) => void;
}> = ({ item, onBack, onStatusChange }) => {
    const [submitting, setSubmitting] = useState(false);
    // The reviewer picks ONE verdict explicitly (a single-select control, not three
    // independently-clickable buttons) before anything fires. Approve needs no
    // explanation; Request Changes/Reject each reveal their own required field once
    // selected - see the "Your Review" panel below, which is deliberately a separate
    // section from Comments rather than a second thing that looks like a comment box.
    const [verdict, setVerdict] = useState<QueueStatus | null>(null);
    const [verdictNote, setVerdictNote] = useState('');
    const [comments, setComments] = useState<ReviewComment[]>([]);
    const [commentsLoading, setCommentsLoading] = useState(true);
    const [commentsError, setCommentsError] = useState('');
    const [newComment, setNewComment] = useState('');
    const [sending, setSending] = useState(false);
    const feedRef = useRef<HTMLDivElement>(null);
    const readOnlySkillEditorRef = useRef<HTMLTextAreaElement>(null);
    const typeInfo = TYPE_CONFIG[item.type] ?? TYPE_CONFIG.skill;
    const loadComments = useCallback(() => {
        setCommentsLoading(true);
        setCommentsError('');
        return fetch(`${(process.env.REACT_APP_Auth_Url || '')}/api/ReviewQueue/${item.id}/comments`, { headers: authHeaders() })
            .then(r => r.ok ? r.json() : Promise.reject(r.status))
            .then(json => {
            const list: any[] = Array.isArray(json) ? json : (json?.data ?? []);
            setComments(list.map((c: any): ReviewComment => ({
                id: String(c.id),
                authorName: c.authorName ?? 'Unknown',
                message: c.message ?? '',
                relatedStatus: c.relatedStatus ?? null,
                createdAt: c.createdAt ?? new Date().toISOString(),
            })));
        })
            .catch(() => {
                setComments([]);
                setCommentsError("Couldn't load review comments. Try again later.");
            })
            .finally(() => setCommentsLoading(false));
    }, [item.id]);
    useEffect(() => { loadComments(); }, [loadComments]);
    useEffect(() => {
        if (feedRef.current)
            feedRef.current.scrollTop = feedRef.current.scrollHeight;
    }, [comments]);
    const handleSendComment = async () => {
        const text = newComment.trim();
        if (!text || sending)
            return;
        setSending(true);
        try {
            const res = await fetch(`${(process.env.REACT_APP_Auth_Url || '')}/api/ReviewQueue/${item.id}/comments`, {
                method: 'POST',
                headers: authHeaders(),
                body: JSON.stringify({ message: text }),
            });
            if (res.ok) {
                const data = await res.json();
                setComments(prev => [...prev, {
                        id: String(data.id ?? Date.now()),
                        authorName: data.authorName ?? 'You',
                        message: text,
                        createdAt: data.createdAt ?? new Date().toISOString(),
                    }]);
                setNewComment('');
            }
        }
        catch { }
        finally {
            setSending(false);
        }
    };
    const handleAction = async (newStatus: QueueStatus, note?: string) => {
        setSubmitting(true);
        try {
            await fetch(`${(process.env.REACT_APP_Auth_Url || '')}/api/ReviewQueue/${item.id}/status`, {
                method: 'POST',
                headers: authHeaders(),
                body: JSON.stringify({ status: newStatus, note: note?.trim() || undefined }),
            });
            // The note (if any) was auto-posted server-side as a tagged comment -
            // reload rather than guess its id/timestamp locally, so it shows up
            // immediately with the right relatedStatus badge.
            if (note?.trim())
                await loadComments();
        }
        catch { }
        finally {
            setSubmitting(false);
            setVerdict(null);
            setVerdictNote('');
            onStatusChange(item.id, newStatus);
        }
    };
    const submitVerdict = () => {
        if (!verdict)
            return;
        handleAction(verdict, verdict === 'approved' ? undefined : verdictNote);
    };
    function initials(name: string) {
        return name.split(' ').map(p => p[0] ?? '').join('').toUpperCase().slice(0, 2);
    }
    function avatarColor(name: string) {
        const colors = ['#8b5cf6', '#14b8a6', '#f59e0b', '#3b82f6', '#ec4899', '#22d3ee'];
        let h = 0;
        for (let i = 0; i < name.length; i++)
            h = (h * 31 + name.charCodeAt(i)) & 0xffff;
        return colors[h % colors.length];
    }
    return (<motion.div initial={{ opacity: 0, x: 32 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: 32 }} transition={{ duration: 0.22 }} style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
      
      <div style={{ flex: 1, overflowY: 'auto', padding: '0 2px 16px' }}>

        
        <button onClick={onBack} style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            color: 'var(--text-tertiary)', fontSize: 12, fontWeight: 500,
            background: 'none', border: 'none', cursor: 'pointer',
            padding: 0, marginBottom: 18, alignSelf: 'flex-start', fontFamily: 'inherit',
        }}>
          ← Back to Queue
        </button>

        
        <div style={{
            background: 'var(--surface-card)', border: '1px solid var(--border-glass)',
            borderRadius: 14, marginBottom: 16, overflow: 'hidden',
        }}>
          
          <div style={{ padding: '14px 20px', borderBottom: '1px solid var(--border-glass)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <div style={{
            width: 40, height: 40, borderRadius: 10, flexShrink: 0,
            background: typeInfo.color + '20', border: `1.5px solid ${typeInfo.color}35`,
            color: typeInfo.color, display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: item.icon ? 18 : undefined,
        }}>
                {item.icon ? item.icon : <typeInfo.Icon size={18}/>}
              </div>
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                  <h2 style={{ color: 'var(--text-primary)', fontSize: 15, fontWeight: 700, margin: 0 }}>{item.name}</h2>
                  <span style={{ background: typeInfo.color + '18', color: typeInfo.color, fontSize: 10, fontWeight: 700, padding: '2px 7px', borderRadius: 4, letterSpacing: '0.05em' }}>
                    {typeInfo.label.toUpperCase()}
                  </span>
                </div>
                <span style={{ color: 'var(--text-tertiary)', fontSize: 11 }}>by {item.creator} · {timeAgo(item.submittedAt)}</span>
              </div>
            </div>
            <span style={{
            padding: '4px 12px', borderRadius: 99, fontSize: 12, fontWeight: 600,
            background: STATUS_CONFIG[item.status].bg,
            border: `1px solid ${STATUS_CONFIG[item.status].border}`,
            color: STATUS_CONFIG[item.status].color,
        }}>
              {STATUS_CONFIG[item.status].label}
            </span>
          </div>

          
          <div style={{ padding: '18px 20px', display: 'flex', flexDirection: 'column', gap: 18 }}>

            
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
              <FieldBlock label="Name" value={item.name}/>
              <FieldBlock label="Category" value={item.category}/>
            </div>

            
            <div>
              <FieldLabel>Description</FieldLabel>
              <div style={{
            background: 'var(--surface-glass)', border: '1px solid var(--border-glass)',
            borderRadius: 10, padding: '10px 14px', minHeight: 72,
        }}>
                {item.description
            ? <p style={{ color: 'var(--text-secondary)', fontSize: 13, lineHeight: 1.7, margin: 0 }}>{item.description}</p>
            : <p style={{ color: 'var(--text-muted)', fontSize: 13, margin: 0, fontStyle: 'italic' }}>No description provided</p>}
              </div>
            </div>

            
            {item.type === 'skill' && (<div>
                <FieldLabel>Skills</FieldLabel>
                <div style={{
                background: 'var(--surface-glass)', border: '1px solid var(--border-glass)',
                borderRadius: 10, padding: '10px 14px', minHeight: 60,
            }}>
                  {item.skillsText && item.skillsText.trim() ? (<ul style={{ margin: 0, paddingLeft: 18 }}>
                      {item.skillsText.split('\n').map(s => s.trim()).filter(Boolean).map((s, i) => (<li key={i} style={{ color: 'var(--text-secondary)', fontSize: 13, lineHeight: 1.65, marginBottom: 2 }}>{s}</li>))}
                    </ul>) : (<p style={{ color: 'var(--text-muted)', fontSize: 13, margin: 0, fontStyle: 'italic' }}>No skills listed</p>)}
                </div>
              </div>)}

            
            {item.type === 'skill' && (() => {
            let files: string[] = [];
            if (item.kbFiles) {
                try {
                    files = JSON.parse(item.kbFiles);
                }
                catch { }
            }
            return (<div>
                  <FieldLabel>Knowledge Base</FieldLabel>
                  {files.length > 0 ? (<div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                      {files.map(f => (<span key={f} style={{
                            background: 'rgba(139,92,246,0.12)', border: '1px solid rgba(139,92,246,0.28)',
                            color: '#a78bfa', fontSize: 12, padding: '4px 10px', borderRadius: 7,
                            display: 'flex', alignItems: 'center', gap: 5,
                        }}>
                          📎 {f}
                        </span>))}
                    </div>) : (<div style={{
                        background: 'var(--surface-input)', border: '1px dashed var(--border-glass)',
                        borderRadius: 10, padding: '14px', textAlign: 'center',
                    }}>
                      <p style={{ color: 'var(--text-muted)', fontSize: 12, margin: 0, fontStyle: 'italic' }}>No knowledge base files attached</p>
                    </div>)}
                </div>);
        })()}

            
            {item.type === 'skill' && item.skillContent && (<div>
                <FieldLabel>Skill content</FieldLabel>
                <SkillEditorBody mode="preview" onModeChange={() => { }} body={item.skillContent} onBodyChange={() => { }} textEditorRef={readOnlySkillEditorRef} insertMd={() => { }} readOnly hideModeTabs/>
              </div>)}

            
            {item.type === 'agent' && item.agentShareToken && (<div>
                <FieldLabel>Agent bundle</FieldLabel>
                <AgentBundlePreview token={item.agentShareToken}/>
              </div>)}

            
            {item.type === 'mcp_server' && (<>
                <FieldBlock label="MCP Server URL" value={item.mcpUrl}/>
                {(() => {
                let headerKeys: string[] = [];
                if (item.mcpHeaders) {
                    try {
                        const parsed = JSON.parse(item.mcpHeaders) as Record<string, string>[];
                        headerKeys = parsed.map(h => Object.keys(h)[0]).filter(Boolean);
                    }
                    catch { }
                }
                return headerKeys.length > 0 ? (<div>
                      <FieldLabel>MCP Headers</FieldLabel>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                        {headerKeys.map(k => (<span key={k} style={{
                            background: 'rgba(59,130,246,0.12)', border: '1px solid rgba(59,130,246,0.28)',
                            color: '#60a5fa', fontSize: 12, padding: '4px 10px', borderRadius: 7,
                        }}>
                            {k}
                          </span>))}
                      </div>
                    </div>) : null;
            })()}
              </>)}

          </div>
        </div>

        
        <div style={{
            background: 'var(--surface-glass)', border: '1px solid var(--border-glass)',
            borderRadius: 12, overflow: 'hidden', marginBottom: 16,
        }}>
          <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--border-subtle)', display: 'flex', alignItems: 'center', gap: 7 }}>
            <FiMessageSquare size={13} style={{ color: 'var(--text-secondary)' }}/>
            <span style={{ color: 'var(--text-secondary)', fontSize: 11, fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase' }}>
              Comments {comments.length > 0 && `(${comments.length})`}
            </span>
          </div>

          
          <div ref={feedRef} style={{ maxHeight: 280, overflowY: 'auto', padding: '12px 16px', display: 'flex', flexDirection: 'column', gap: 14 }}>
            {commentsLoading ? (<p style={{ color: 'var(--text-tertiary)', fontSize: 12, textAlign: 'center', margin: '8px 0' }}>Loading…</p>) : commentsError ? (<p style={{ color: '#f87171', fontSize: 12, textAlign: 'center', margin: '8px 0' }}>{commentsError}</p>) : comments.length === 0 ? (<p style={{ color: 'var(--text-muted)', fontSize: 12, textAlign: 'center', margin: '8px 0' }}>No comments yet. Be the first to add one.</p>) : comments.map(c => {
            const color = avatarColor(c.authorName);
            // The note auto-posted alongside a verdict gets its own color and a
            // label tag, so it reads as "the official reason" rather than blending
            // into ordinary back-and-forth - reuses STATUS_CONFIG rather than
            // inventing a second color mapping for the same two statuses.
            const verdictCfg = c.relatedStatus ? STATUS_CONFIG[c.relatedStatus] : null;
            return (<div key={c.id} style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>

                  <div style={{
                    width: 30, height: 30, borderRadius: '50%', flexShrink: 0,
                    background: color + '25', border: `1.5px solid ${color}55`,
                    color, display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: 11, fontWeight: 700,
                }}>
                    {initials(c.authorName)}
                  </div>

                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4, flexWrap: 'wrap' }}>
                      <span style={{ color: 'var(--text-secondary)', fontSize: 12, fontWeight: 600 }}>{c.authorName}</span>
                      <span style={{ color: 'var(--text-tertiary)', fontSize: 11 }}>{timeAgo(c.createdAt)}</span>
                      {verdictCfg && (<span style={{
                        padding: '1px 8px', borderRadius: 99, fontSize: 10, fontWeight: 700,
                        background: verdictCfg.bg, border: `1px solid ${verdictCfg.border}`, color: verdictCfg.color,
                        textTransform: 'uppercase', letterSpacing: '0.03em',
                    }}>
                          {verdictCfg.label}
                        </span>)}
                    </div>
                    <div style={{
                    background: verdictCfg ? verdictCfg.bg : 'var(--surface-card)',
                    border: `1px solid ${verdictCfg ? verdictCfg.border : 'var(--border-glass)'}`,
                    borderRadius: '0 10px 10px 10px', padding: '8px 12px',
                }}>
                      <p style={{ color: 'var(--text-secondary)', fontSize: 13, lineHeight: 1.6, margin: 0, whiteSpace: 'pre-wrap' }}>{c.message}</p>
                    </div>
                  </div>
                </div>);
        })}
          </div>

          
          <div style={{ padding: '10px 16px', borderTop: '1px solid var(--border-subtle)', display: 'flex', gap: 8, alignItems: 'flex-end' }}>
            <textarea value={newComment} onChange={e => setNewComment(e.target.value)} onKeyDown={e => { if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
        e.preventDefault();
        handleSendComment();
    } }} placeholder="Add a comment… (Ctrl+Enter to send)" rows={2} style={{
            flex: 1, background: 'var(--surface-card)', border: '1px solid var(--border-glass)',
            borderRadius: 10, color: 'var(--text-secondary)', fontSize: 13, padding: '8px 12px',
            fontFamily: 'inherit', resize: 'none', outline: 'none', boxSizing: 'border-box',
        }}/>
            <button onClick={handleSendComment} disabled={!newComment.trim() || sending} style={{
            width: 36, height: 36, borderRadius: 10, border: 'none', flexShrink: 0,
            background: newComment.trim() && !sending ? 'linear-gradient(135deg, #7c3aed, #6d28d9)' : 'var(--surface-glass-hover)',
            color: newComment.trim() && !sending ? '#fff' : 'var(--text-tertiary)',
            cursor: newComment.trim() && !sending ? 'pointer' : 'not-allowed',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: newComment.trim() && !sending ? '0 2px 8px rgba(124,58,237,0.4)' : 'none',
            transition: 'all 150ms',
        }}>
              <FiSend size={14}/>
            </button>
          </div>
        </div>

      </div>



      {/* Deliberately its own bordered panel, separate from Comments above, so
          picking a verdict never reads as "a second comment box." Selecting a
          pill doesn't fire anything by itself - it only reveals what that verdict
          needs, exactly like Comments never fires until Send is pressed. Approve
          needs nothing further; Request Changes/Reject each get their own
          contextually-labeled required reason, which the backend auto-posts into
          the thread above tagged with that verdict (see the badge on that comment). */}
      <div style={{
            background: 'var(--surface-glass)', border: '1px solid var(--border-glass)',
            borderRadius: 12, overflow: 'hidden', flexShrink: 0,
        }}>
        <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--border-subtle)', display: 'flex', alignItems: 'center', gap: 7 }}>
          <FiCheckSquare size={13} style={{ color: 'var(--text-secondary)' }}/>
          <span style={{ color: 'var(--text-secondary)', fontSize: 11, fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase' }}>
            Your Review
          </span>
        </div>

        <div style={{ padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: 12 }}>

          <div style={{ display: 'flex', gap: 8 }}>
            {(['approved', 'changes_requested', 'rejected'] as const).map(v => {
                const cfg = STATUS_CONFIG[v];
                const active = verdict === v;
                const alreadyThere = item.status === v;
                const isDisabled = submitting || alreadyThere;
                return (<button key={v} type="button" disabled={isDisabled} onClick={() => { setVerdict(active ? null : v); setVerdictNote(''); }} title={alreadyThere ? `Already ${cfg.label.toLowerCase()}` : undefined} style={{
                        flex: 1, padding: '9px 0', borderRadius: 9, fontSize: 13, fontWeight: 600,
                        border: `1px solid ${active ? cfg.color : cfg.border}`,
                        background: active ? cfg.color : cfg.bg,
                        color: active ? '#fff' : cfg.color,
                        cursor: isDisabled ? 'not-allowed' : 'pointer', fontFamily: 'inherit',
                        opacity: isDisabled ? 0.5 : 1, transition: 'all 120ms',
                    }}>
                    {alreadyThere ? `✓ ${v === 'approved' ? 'Approved' : v === 'changes_requested' ? 'Changes Requested' : 'Rejected'}` : v === 'approved' ? 'Approve' : v === 'changes_requested' ? 'Request Changes' : 'Reject'}
                  </button>);
            })}
          </div>


          {(verdict === 'changes_requested' || verdict === 'rejected') && (<div>
              <label style={{ display: 'block', color: 'var(--text-tertiary)', fontSize: 11, fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: 6 }}>
                {verdict === 'rejected' ? 'Why is this being rejected?' : 'What needs to change before this can be approved?'}
              </label>
              <textarea autoFocus value={verdictNote} onChange={e => setVerdictNote(e.target.value)} placeholder="This is sent to the creator as a comment on the review thread…" rows={3} style={{
                width: '100%', background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.09)',
                borderRadius: 9, color: 'var(--text-primary)', fontSize: 13, padding: '8px 11px',
                fontFamily: 'inherit', resize: 'none', outline: 'none', boxSizing: 'border-box',
              }}/>
            </div>)}


          {verdict && (() => {
                const cfg = STATUS_CONFIG[verdict];
                const needsNote = verdict !== 'approved';
                const ready = !needsNote || !!verdictNote.trim();
                const label = verdict === 'approved' ? 'Approve' : verdict === 'changes_requested' ? 'Request Changes' : 'Reject';
                return (<button disabled={submitting || !ready} onClick={submitVerdict} style={{
                    padding: '10px 0', borderRadius: 10, border: 'none',
                    background: cfg.color, color: '#fff', fontSize: 13, fontWeight: 600,
                    cursor: submitting || !ready ? 'not-allowed' : 'pointer',
                    fontFamily: 'inherit', opacity: submitting || !ready ? 0.55 : 1,
                }}>
                  {label}
                </button>);
            })()}
        </div>
      </div>
    </motion.div>);
};
const ReviewQueuePage: React.FC = () => {
    const [items, setItems] = useState<QueueItem[]>([]);
    const [loading, setLoading] = useState(false);
    const [selected, setSelected] = useState<QueueItem | null>(null);
    const fetchItems = useCallback(async () => {
        setLoading(true);
        try {
            const res = await fetch(`${(process.env.REACT_APP_Auth_Url || '')}/api/ReviewQueue/platform`, { headers: authHeaders() });
            if (!res.ok)
                throw new Error();
            const json = await res.json();
            const list: any[] = Array.isArray(json) ? json : (json?.data ?? []);
            setItems(list.map((r: any): QueueItem => ({
                id: String(r.id),
                toolId: r.toolId ? String(r.toolId) : undefined,
                name: r.name ?? '',
                version: r.version || undefined,
                isPriority: r.isPriority ?? false,
                type: r.type ?? 'skill',
                creator: r.creatorName ?? r.creator ?? '',
                creatorStatus: r.creatorStatus ?? 'offline',
                visibility: r.visibility ?? 'public',
                submittedAt: r.submittedAt ?? new Date().toISOString(),
                status: r.status ?? 'pending',
                description: r.description || '',
                category: r.category || '',
                icon: r.icon || '',
                skillsText: r.skillsText || '',
                kbFiles: r.kbFiles || '',
                skillContent: r.skillContent || '',
                mcpUrl: r.mcpUrl || '',
                mcpHeaders: r.mcpHeaders || '',
                agentShareToken: r.agentShareToken || '',
            })));
        }
        catch {
            setItems([]);
        }
        finally {
            setLoading(false);
        }
    }, []);
    useEffect(() => { fetchItems(); }, [fetchItems]);
    const handleStatusChange = (id: string, status: QueueStatus) => {
        setItems(prev => prev.map(it => it.id === id ? { ...it, status } : it));
        if (selected?.id === id)
            setSelected(prev => prev ? { ...prev, status } : prev);
    };
    return (<div style={{ height: '100%', overflowY: 'auto', padding: '28px 32px', display: 'flex', flexDirection: 'column' }}>

      <AnimatePresence mode="wait">
        {selected ? (<ReviewDetail key="detail" item={selected} onBack={() => setSelected(null)} onStatusChange={handleStatusChange}/>) : (<motion.div key="list" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.15 }}>

            <h3 style={{ color: 'var(--text-primary)', fontSize: 16, fontWeight: 700, margin: '0 0 20px' }}>Platform Review</h3>

            
            <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.18 }}>
              <div style={{ background: 'var(--surface-card)', border: '1px solid var(--border-glass)', borderRadius: 16, overflow: 'hidden' }}>

                
                <div style={{ display: 'grid', gridTemplateColumns: '2.5fr 80px 1.6fr 100px 110px 180px 110px', padding: '12px 20px', borderBottom: '1px solid var(--border-subtle)' }}>
                  {['NAME', 'TYPE', 'CREATOR', 'VISIBILITY', 'SUBMITTED', 'STATUS', ''].map((col) => (<span key={col} style={{ color: 'var(--text-tertiary)', fontSize: 11, fontWeight: 600, letterSpacing: '0.07em' }}>{col}</span>))}
                </div>

                
                {loading ? (<div style={{ padding: '40px 20px', textAlign: 'center', color: 'var(--text-tertiary)', fontSize: 13 }}>Loading…</div>) : items.length === 0 ? (<div style={{ padding: '48px 20px', textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}>No items in review queue</div>) : items.map((item, idx) => {
                const status = STATUS_CONFIG[item.status];
                const typeInfo = TYPE_CONFIG[item.type] ?? TYPE_CONFIG.skill;
                return (<motion.div key={item.id} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: idx * 0.04 }} style={{
                        display: 'grid', gridTemplateColumns: '2.5fr 80px 1.6fr 100px 110px 180px 110px',
                        padding: '16px 20px', alignItems: 'center',
                        borderBottom: idx < items.length - 1 ? '1px solid var(--border-subtle)' : 'none',
                    }}>
                      
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
                        <span style={{ color: 'var(--text-primary)', fontSize: 14, fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{item.name}</span>
                        {item.version && <span style={{ background: 'rgba(99,102,241,0.2)', border: '1px solid rgba(99,102,241,0.4)', color: '#818cf8', fontSize: 10, fontWeight: 700, padding: '1px 6px', borderRadius: 4, flexShrink: 0 }}>{item.version}</span>}
                        {item.isPriority && <span style={{ background: 'rgba(244,63,94,0.15)', border: '1px solid rgba(244,63,94,0.4)', color: '#fb7185', fontSize: 10, fontWeight: 700, padding: '1px 6px', borderRadius: 4, flexShrink: 0 }}>PRIORITY</span>}
                      </div>
                      
                      <div>
                        <div style={{ width: 32, height: 32, borderRadius: 8, background: typeInfo.color + '18', border: `1px solid ${typeInfo.color}30`, color: typeInfo.color, display: 'flex', alignItems: 'center', justifyContent: 'center' }}><typeInfo.Icon size={16}/></div>
                      </div>
                      
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span style={{ color: 'var(--text-secondary)', fontSize: 13 }}>{item.creator}</span>
                        <span style={{ width: 7, height: 7, borderRadius: '50%', background: CREATOR_DOT[item.creatorStatus], flexShrink: 0 }}/>
                      </div>
                      
                      <div><FiGlobe size={15} style={{ color: 'var(--text-tertiary)' }}/></div>
                      
                      <div><span style={{ color: 'var(--text-tertiary)', fontSize: 13 }}>{timeAgo(item.submittedAt)}</span></div>
                      
                      <div>
                        <span style={{ display: 'inline-block', padding: '4px 12px', borderRadius: 99, background: status.bg, border: `1px solid ${status.border}`, color: status.color, fontSize: 12, fontWeight: 600 }}>
                          {status.label}
                        </span>
                      </div>
                      
                      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                        <button onClick={() => setSelected(item)} style={{ background: 'linear-gradient(135deg, #6d28d9, #7c3aed)', color: '#fff', border: 'none', padding: '8px 22px', borderRadius: 10, fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', boxShadow: '0 2px 8px rgba(109,40,217,0.35)' }}>
                          Review
                        </button>
                      </div>
                    </motion.div>);
            })}
              </div>
            </motion.div>
          </motion.div>)}
      </AnimatePresence>
    </div>);
};
export default ReviewQueuePage;
